import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Camera,
  Mail,
  MapPin,
  Globe,
  Loader2,
  Smartphone,
  Save,
  Hash,
  Edit2,
  CheckCircle2,
  User,
  FileText,
  ShieldCheck,
  ChevronRight,
  X,
  Check,
} from "lucide-react";
import Activity from "../components/Dashboardloader.jsx";
import { toast } from "sonner";
import Swal from "sweetalert2";
import imageCompression from "browser-image-compression";
import api, { IMAGE_BASE_URL } from "../api/Api.js";

const ProfilePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  // Address Edit State
  const [editableFields, setEditableFields] = useState({
    zipcode: false,
    address1: false,
    address2: false,
    address3: false,
  });

  // Modal & OTP State
  const [modalType, setModalType] = useState(null);
  const [tempValue, setTempValue] = useState("");
  const [otpValue, setOtpValue] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);

  // Data State
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [preview, setPreview] = useState(null);

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    utype: "",
    address1: "",
    address2: "",
    address3: "",
    country_id: "99",
    state_id: "",
    city_id: "",
    zipcode: "",
    photo: null,
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [profileRes, statesRes] = await Promise.all([
        api.get("/auth/profile"),
        api.get("/auth/geo/states"),
      ]);

      const p = profileRes.data.success ? profileRes.data : profileRes.data;
      const initialStates = statesRes.data.data || [];
      setStates(initialStates);

      setFormData({
        fullName: p.fullName || "",
        driverId: p.driverId || "",
        email: p.email || "",
        phone: p.phone || "",
        utype: p.utype || "",
        address1: p.address1 || "",
        address2: p.address2 || "",
        address3: p.address3 || "",
        country_id: "99",
        state_id: p.state_id || "",
        city_id: p.city_id || "",
        zipcode: p.zipcode || "",
        photo: null,
      });

      if (p.state_id) {
        const citiesRes = await api.get(
          `/auth/geo/cities?stateCode=${p.state_id}`,
        );
        setCities(citiesRes.data.data || []);
      }

      if (p.driver_photo) {
        const sanitizedPath = p.driver_photo.startsWith("/")
          ? p.driver_photo
          : `/${p.driver_photo}`;
          // console.log("Sanitized Photo Path:", sanitizedPath);
          // console.log("Full Image URL:", `${IMAGE_BASE_URL}${sanitizedPath}`);
        setPreview(`${IMAGE_BASE_URL}${sanitizedPath}`);
      }
    } catch (err) {
      console.error("Profile load error:", err);
      toast.error("Error loading profile");
    } finally {
      setLoading(false);
    }
  };

  const toggleEdit = (field) => {
    setEditableFields((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleStateChange = async (e) => {
    const selectedStateCode = e.target.value;
    setFormData((prev) => ({
      ...prev,
      state_id: selectedStateCode,
      city_id: "",
    }));

    if (selectedStateCode) {
      try {
        const res = await api.get(
          `/auth/geo/cities?stateCode=${selectedStateCode}`,
        );
        setCities(res.data.data || []);
      } catch (err) {
        toast.error("Failed to load cities");
      }
    } else {
      setCities([]);
    }
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image too large. Max size is 5MB.");
      return;
    }

    const options = {
      maxSizeMB: 0.1,
      maxWidthOrHeight: 800,
      useWebWorker: true,
    };

    try {
      const compressedFile = await imageCompression(file, options);
      setFormData((prev) => ({ ...prev, photo: compressedFile }));
      setPreview(URL.createObjectURL(compressedFile));
    } catch (error) {
      console.error("Compression Error:", error);
      setFormData((prev) => ({ ...prev, photo: file }));
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleSaveProfile = async () => {
    navigator.vibrate?.(60);
    setUpdating(true);

    // 1. NATIVE LOADING ALERT (Wider & cleaner)
    Swal.fire({
      width: 260,
      background: "rgba(255, 255, 255, 0.9)",
      backdrop: "rgba(0,0,0,0.3)",
      html: `
        <div class="d-flex flex-column align-items-center justify-content-center py-3">
          <div class="spinner-border text-primary mb-3" style="width: 2.2rem; height: 2.2rem; border-width: 0.2em;" role="status"></div>
          <div style="font-size: 15px; font-weight: 600; color: #1c1c1e;">Updating Profile...</div>
        </div>
      `,
      showConfirmButton: false,
      allowOutsideClick: false,
      customClass: { popup: "native-swal-popup" },
    });

    const data = new FormData();

    Object.keys(formData).forEach((key) => {
      if (key === "photo") {
        if (formData.photo) data.append("photo", formData.photo);
      } else {
        data.append(key, formData[key] || "");
      }
    });

    try {
      const res = await api.put("/auth/update-complete-profile", data);
      navigator.vibrate?.(120);

      // 2. NATIVE SUCCESS ALERT (Standard Mobile Width: 320px)
      await Swal.fire({
        width: 320,
        background: "rgba(255, 255, 255, 0.95)",
        backdrop: "rgba(0,0,0,0.3)",
        html: `
          <div class="d-flex flex-column align-items-center justify-content-center pt-2 pb-3 px-3">
            <div class="bg-success bg-opacity-10 text-success rounded-circle d-flex align-items-center justify-content-center mb-3 mt-2" style="width: 64px; height: 64px;">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
            </div>
            <div style="font-size: 19px; font-weight: 700; color: #1c1c1e;">Success</div>
            <div style="font-size: 14px; color: #8e8e93; margin-top: 6px; text-align: center;">
              ${res.data?.message || "Profile and address updated successfully."}
            </div>
          </div>
        `,
        showConfirmButton: false,
        timer: 1800,
        customClass: { popup: "native-swal-popup" },
      });

      setEditableFields({
        zipcode: false,
        address1: false,
        address2: false,
        address3: false,
      });
      setUpdating(false);
      navigate("/dashboard");
    } catch (e) {
      navigator.vibrate?.(200);
      console.error("Profile update error:", e);

      // 3. NATIVE ERROR ALERT (Standard Mobile Width: 320px)
      await Swal.fire({
        width: 320,
        padding: "24px 0 0 0",
        background: "rgba(255, 255, 255, 0.95)",
        backdrop: "rgba(0,0,0,0.4)",
        html: `
          <div class="px-4 pb-3">
            <div style="font-size: 18px; font-weight: 600; color: #000; text-align: center; margin-bottom: 8px;">Update Failed</div>
            <div style="font-size: 14px; color: #3c3c43; text-align: center; line-height: 1.4;">
              ${e.response?.data?.message || "Unable to update profile. Please try again."}
            </div>
          </div>
        `,
        buttonsStyling: false,
        confirmButtonText: "OK",
        customClass: {
          popup: "native-swal-popup",
          actions: "native-swal-actions",
          confirmButton: "native-swal-button",
        },
      });

      setUpdating(false);
    }
  };

  const handleRequestOtp = async () => {
    if (!tempValue) return toast.error(`Enter valid ${modalType}`);
    setOtpLoading(true);
    try {
      const endpoint =
        modalType === "phone"
          ? "/auth/request-phone-change"
          : "/auth/request-email-change";
      const payload =
        modalType === "phone"
          ? { newMobileNumber: tempValue }
          : { newEmail: tempValue };
      const res = await api.post(endpoint, payload);

      if (res.data.dev_otp)
        toast(`Dev OTP: ${res.data.dev_otp}`, { icon: "🔑" });

      setOtpSent(true);
      toast.success("OTP sent successfully!");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send OTP");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otpValue.length !== 4) return toast.error("Enter 4-digit OTP");
    setOtpLoading(true);
    try {
      const endpoint =
        modalType === "phone"
          ? "/auth/verify-phone-change"
          : "/auth/verify-email-change";
      const payload =
        modalType === "phone"
          ? { newMobileNumber: tempValue, otp: otpValue }
          : { newEmail: tempValue, otp: otpValue };
      await api.post(endpoint, payload);

      setFormData((prev) => ({
        ...prev,
        [modalType === "phone" ? "phone" : "email"]: tempValue,
      }));
      toast.success(
        `${modalType === "phone" ? "Phone" : "Email"} updated successfully!`,
      );
      closeModal();
    } catch (err) {
      toast.error(err.response?.data?.message || "Invalid OTP");
    } finally {
      setOtpLoading(false);
    }
  };

  const closeModal = () => {
    setModalType(null);
    setTempValue("");
    setOtpValue("");
    setOtpSent(false);
  };

  if (loading) {
    return (
      <div className="loader-container">
        <Activity />
        <style>{`
            .loader-container {
                position: fixed; 
                top: 0;
                left: 0;
                width: 100%;
                height: 100%; 
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                background: #f8f9fa; 
            }
        `}</style>
      </div>
    );
  }

  return (
    <div className="min-vh-100 bg-white pb-2 font-inter">
      {/* Top Header Gradient */}
      <div className="text-white pt-4 pb-5 px-3 rounded-bottom-4 header-gradient">
        <div className="container-fluid mx-auto" style={{ maxWidth: "600px" }}>
          <h4 className="fw-bold mb-1">My Profile</h4>
          <p className="mb-0 opacity-75 small">Manage your account details</p>
        </div>
      </div>

      <div
        className="container-fluid px-3 mx-auto"
        style={{ maxWidth: "600px", marginTop: "-45px" }}
      >
        {/* Floating Avatar Card */}
        <div className="card border-0 shadow-sm rounded-4 mb-3">
          <div className="card-body p-4 text-center">
            <div className="position-relative d-inline-block mb-3">
              {/* Profile Ring */}
              <div className="rounded-circle p-1 d-inline-block avatar-ring">
                <div
                  className="rounded-circle overflow-hidden border border-3 border-white bg-light d-flex align-items-center justify-content-center"
                  style={{ width: "105px", height: "105px" }}
                >
                  {preview ? (
                    <img
                      src={preview}
                      alt="Profile"
                      className="w-100 h-100 object-fit-cover"
                    />
                  ) : (
                    <span className="fs-1 fw-bold text-primary">
                      {formData.fullName
                        ? formData.fullName.charAt(0).toUpperCase()
                        : "U"}
                    </span>
                  )}
                </div>
              </div>

              {/* Camera Button */}
              <label
                htmlFor="photo"
                className="position-absolute bottom-0 end-0 bg-primary text-white rounded-circle d-flex align-items-center justify-content-center border border-2 border-white shadow-sm cursor-pointer hover-scale"
                style={{ width: "36px", height: "36px" }}
              >
                <Camera size={16} strokeWidth={2.5} />
                <input
                  id="photo"
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handlePhotoChange}
                />
              </label>
            </div>

            <h4 className="fw-bold text-dark mb-1">
              {formData.fullName || "Driver Name"}
            </h4>
            <div className="d-flex align-items-center justify-content-center gap-2 mb-3">
              <span className="badge bg-light text-primary rounded-pill px-3 py-2 fw-semibold">
                {formData.driverId || "--"}
              </span>
              <span className="text-muted small">{formData.phone || "--"}</span>
            </div>

            <div className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 rounded-pill px-3 py-2 fw-bold">
              <CheckCircle2 size={16} className="me-1 mb-1" /> Verified Account
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <h6 className="text-muted fw-bold small text-uppercase mb-2 px-1 d-flex align-items-center gap-2">
          <ShieldCheck size={16} /> Quick Actions
        </h6>
        <div className="card border-0 shadow-sm rounded-4 mb-4 overflow-hidden">
          <div className="list-group list-group-flush">
            <div
              className="list-group-item d-flex align-items-center p-3 border-bottom cursor-pointer hover-bg"
              onClick={() => navigate("/documents")}
            >
              <div className="bg-danger bg-gradient text-white p-2 rounded-3 me-3">
                <FileText size={20} />
              </div>
              <div className="flex-grow-1">
                <div className="fw-bold text-dark">Documents</div>
                <div className="small text-muted">Manage your documents</div>
              </div>
              <ChevronRight size={18} className="text-muted" />
            </div>

            <div
              className="list-group-item d-flex align-items-center p-3 cursor-pointer hover-bg"
              onClick={() => navigate("/reset-mpin")}
            >
              <div className="bg-warning bg-gradient text-white p-2 rounded-3 me-3">
                <ShieldCheck size={20} />
              </div>
              <div className="flex-grow-1">
                <div className="fw-bold text-dark">Reset MPIN</div>
                <div className="small text-muted">Update security PIN</div>
              </div>
              <ChevronRight size={18} className="text-muted" />
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <h6 className="text-muted fw-bold small text-uppercase mb-2 px-1 d-flex align-items-center gap-2">
          <User size={16} /> Contact Information
        </h6>
        <div className="card border-0 shadow-sm rounded-4 mb-4 overflow-hidden">
          <div className="list-group list-group-flush">
            <div className="list-group-item d-flex align-items-center p-3 border-bottom">
              <div className="bg-primary bg-opacity-10 text-primary p-2 rounded-3 me-3">
                <Smartphone size={20} />
              </div>
              <div className="flex-grow-1">
                <div className="small text-muted fw-semibold text-uppercase">
                  Mobile Number
                </div>
                <div className="fw-bold text-dark">
                  {formData.phone || "---"}
                </div>
              </div>
              <button
                className="btn btn-light text-primary fw-bold rounded-pill btn-sm px-3 shadow-sm"
                onClick={() => {
                  setModalType("phone");
                  setTempValue(formData.phone);
                }}
              >
                Change
              </button>
            </div>

            <div className="list-group-item d-flex align-items-center p-3">
              <div className="bg-primary bg-opacity-10 text-primary p-2 rounded-3 me-3">
                <Mail size={20} />
              </div>
              <div className="flex-grow-1 overflow-hidden pe-2">
                <div className="small text-muted fw-semibold text-uppercase">
                  Email Address
                </div>
                <div className="fw-bold text-dark text-truncate">
                  {formData.email || "---"}
                </div>
              </div>
              <button
                className="btn btn-light text-primary fw-bold rounded-pill btn-sm px-3 shadow-sm"
                onClick={() => {
                  setModalType("email");
                  setTempValue(formData.email);
                }}
              >
                Update
              </button>
            </div>
          </div>
        </div>

        {/* Address Details */}
        <h6 className="text-muted fw-bold small text-uppercase mb-2 px-1 d-flex align-items-center gap-2">
          <MapPin size={16} /> Address Details
        </h6>
        <div className="card border-0 shadow-sm rounded-4 mb-4">
          <div className="card-body p-4">
            {/* State */}
            <div className="mb-3">
              <label className="form-label small fw-bold text-muted text-uppercase mb-1">
                State
              </label>
              <div className="input-group bg-light rounded-3 overflow-hidden border border-light">
                <span className="input-group-text bg-transparent border-0 text-primary">
                  <Globe size={18} />
                </span>
                <select
                  className="form-select bg-transparent border-0 fw-semibold text-dark shadow-none py-2"
                  value={formData.state_id}
                  onChange={handleStateChange}
                >
                  <option value="">Select State</option>
                  {states.map((s) => (
                    <option key={s.state_id} value={s.state_id}>
                      {s.state_name || s.state}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* City */}
            <div className="mb-3">
              <label className="form-label small fw-bold text-muted text-uppercase mb-1">
                City
              </label>
              <div className="input-group bg-light rounded-3 overflow-hidden border border-light">
                <span className="input-group-text bg-transparent border-0 text-primary">
                  <MapPin size={18} />
                </span>
                <select
                  className="form-select bg-transparent border-0 fw-semibold text-dark shadow-none py-2"
                  value={formData.city_id}
                  onChange={(e) =>
                    setFormData({ ...formData, city_id: e.target.value })
                  }
                >
                  <option value="">Select City</option>
                  {cities.map((c) => (
                    <option key={c.city_id} value={c.city_id}>
                      {c.city_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Zipcode */}
            <div className="mb-3">
              <label className="form-label small fw-bold text-muted text-uppercase mb-1">
                Zipcode
              </label>
              <div
                className={`input-group bg-light rounded-3 overflow-hidden border ${editableFields.zipcode ? "border-primary bg-white" : "border-light"}`}
              >
                <span className="input-group-text bg-transparent border-0 text-primary">
                  <Hash size={18} />
                </span>
                <input
                  type="text"
                  inputMode="numeric"
                  className="form-control bg-transparent border-0 fw-semibold text-dark shadow-none py-2"
                  placeholder="6-digit Zipcode"
                  value={formData.zipcode}
                  onChange={(e) =>
                    /^\d*$/.test(e.target.value) &&
                    e.target.value.length <= 6 &&
                    setFormData({ ...formData, zipcode: e.target.value })
                  }
                  disabled={!editableFields.zipcode}
                />
                <button
                  className="btn btn-link text-decoration-none px-3"
                  type="button"
                  onClick={() => toggleEdit("zipcode")}
                >
                  {editableFields.zipcode ? (
                    <Check size={20} className="text-success" />
                  ) : (
                    <Edit2 size={16} className="text-muted" />
                  )}
                </button>
              </div>
            </div>

            {/* Address Lines */}
            {[
              {
                label: "Address Line 1",
                key: "address1",
                placeholder: "House/Flat No",
              },
              {
                label: "Address Line 2",
                key: "address2",
                placeholder: "Street/Locality",
              },
              {
                label: "Address Line 3",
                key: "address3",
                placeholder: "Landmark",
              },
            ].map((field) => (
              <div className="mb-3" key={field.key}>
                <label className="form-label small fw-bold text-muted text-uppercase mb-1">
                  {field.label}
                </label>
                <div
                  className={`input-group bg-light rounded-3 overflow-hidden border ${editableFields[field.key] ? "border-primary bg-white" : "border-light"}`}
                >
                  <input
                    type="text"
                    className="form-control bg-transparent border-0 fw-semibold text-dark shadow-none py-2 ps-3"
                    placeholder={field.placeholder}
                    value={formData[field.key]}
                    onChange={(e) =>
                      setFormData({ ...formData, [field.key]: e.target.value })
                    }
                    disabled={!editableFields[field.key]}
                  />
                  <button
                    className="btn btn-link text-decoration-none px-3"
                    type="button"
                    onClick={() => toggleEdit(field.key)}
                  >
                    {editableFields[field.key] ? (
                      <Check size={20} className="text-success" />
                    ) : (
                      <Edit2 size={16} className="text-muted" />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Save Button is now in standard flow (Not Fixed) */}
        <div className="mt-4 mb-5">
          <button
            className="btn btn-primary w-100 py-3 rounded-pill fw-bold shadow-sm d-flex align-items-center justify-content-center gap-2"
            onClick={handleSaveProfile}
            disabled={updating}
            style={{
              background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
              border: "none",
            }}
          >
            {updating ? (
              <>
                <Loader2 className="spinner-border spinner-border-sm" />{" "}
                UPDATING...
              </>
            ) : (
              <>
                <Save size={20} /> SAVE CHANGES
              </>
            )}
          </button>
        </div>
      </div>

      {/* OTP Modal */}
      {modalType && (
        <div
          className="modal show d-block"
          style={{
            backgroundColor: "rgba(0,0,0,0.5)",
            backdropFilter: "blur(4px)",
            zIndex: 1050,
          }}
        >
          <div className="modal-dialog modal-dialog-centered px-3">
            <div className="modal-content border-0 rounded-4 shadow-lg p-4">
              <button
                onClick={closeModal}
                className="btn-close position-absolute top-0 end-0 m-3 shadow-none"
              ></button>

              <div className="text-center mb-4 mt-2">
                <div className="bg-primary bg-opacity-10 text-primary d-inline-flex p-3 rounded-circle mb-3">
                  {modalType === "phone" ? (
                    <Smartphone size={32} />
                  ) : (
                    <Mail size={32} />
                  )}
                </div>
                <h4 className="fw-bold text-dark">
                  Update {modalType === "phone" ? "Mobile" : "Email"}
                </h4>
                <p className="text-muted small">
                  {otpSent
                    ? "Enter the verification code sent to you"
                    : `Enter your new ${modalType === "phone" ? "phone number" : "email address"}`}
                </p>
              </div>

              {!otpSent ? (
                <>
                  <div className="mb-3">
                    <input
                      type={modalType === "phone" ? "tel" : "email"}
                      inputMode={modalType === "phone" ? "numeric" : "email"}
                      className="form-control form-control-lg bg-light border-0 rounded-3 shadow-none px-3"
                      placeholder={
                        modalType === "phone"
                          ? "Enter phone number"
                          : "Enter email address"
                      }
                      value={tempValue}
                      onChange={(e) => {
                        if (modalType === "phone") {
                          if (/^\d*$/.test(e.target.value))
                            setTempValue(e.target.value);
                        } else {
                          setTempValue(e.target.value);
                        }
                      }}
                    />
                  </div>
                  <button
                    className="btn btn-primary w-100 py-3 rounded-pill fw-bold"
                    onClick={handleRequestOtp}
                    disabled={otpLoading}
                    style={{
                      background:
                        "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                      border: "none",
                    }}
                  >
                    {otpLoading ? (
                      <Loader2 className="spinner-border spinner-border-sm" />
                    ) : (
                      "SEND VERIFICATION CODE"
                    )}
                  </button>
                </>
              ) : (
                <>
                  <div className="mb-4 text-center">
                    <input
                      type="text"
                      inputMode="numeric"
                      className="form-control form-control-lg bg-light border-0 rounded-3 shadow-none text-center fw-bold"
                      maxLength={4}
                      placeholder="••••"
                      value={otpValue}
                      onChange={(e) => {
                        if (/^\d*$/.test(e.target.value))
                          setOtpValue(e.target.value);
                      }}
                      style={{ letterSpacing: "12px", fontSize: "24px" }}
                      autoFocus
                    />
                  </div>
                  <button
                    className="btn btn-success w-100 py-3 rounded-pill fw-bold"
                    onClick={handleVerifyOtp}
                    disabled={otpLoading}
                  >
                    {otpLoading ? (
                      <Loader2 className="spinner-border spinner-border-sm" />
                    ) : (
                      "VERIFY & SAVE"
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Very minor utility styles */}
      <style>{`
        .font-inter { font-family: 'Inter', -apple-system, sans-serif; }
        .cursor-pointer { cursor: pointer; }
        .hover-bg:hover { background-color: #f8f9fa; }
        .hover-scale { transition: transform 0.2s; }
        .hover-scale:active { transform: scale(0.9); }
        .header-gradient { background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%); }
        .avatar-ring { background: linear-gradient(135deg, #6366f1, #ec4899); }
        /* Disable input highlight outlines */
        .form-control:focus, .form-select:focus { border-color: transparent; box-shadow: none; }

        .native-swal-popup {
          border-radius: 18px !important;
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          box-shadow: 0 10px 40px rgba(0,0,0,0.15) !important;
        }
        
        .native-swal-actions {
          margin: 0 !important;
          width: 100% !important;
          border-top: 1px solid rgba(0,0,0,0.1) !important;
        }
        
        .native-swal-button {
          width: 100% !important;
          margin: 0 !important;
          background: transparent !important;
          color: #007aff !important; /* Native iOS Blue */
          font-size: 16px !important;
          font-weight: 600 !important;
          padding: 14px !important;
          border: none !important;
          border-radius: 0 0 18px 18px !important;
          transition: background-color 0.2s;
        }
        
        .native-swal-button:active {
          background-color: rgba(0,0,0,0.05) !important;
        }
      `}</style>
    </div>
  );
};

export default ProfilePage;